/**
 * Regras puras dos tipos de campo complementar que precisam de mais do que um
 * `<input>`: link (URL validada) e escolha em lista/tag.
 *
 * Fica aqui, e nao na UI, porque API, web e mobile precisam concordar sobre o
 * que e uma URL aceitavel e sobre onde mora o formato de exibicao do campo.
 */

/** Como uma escolha aparece: menu suspenso ou tags. Muda so o layout. */
export type LayoutEscolha = 'list' | 'tag'

/**
 * Le o layout de um campo de escolha. Default `list` — campos criados antes de
 * existir a tag continuam do jeito que estavam.
 */
export function layoutDaEscolha(
  config: { layout?: unknown; [k: string]: unknown } | null | undefined,
): LayoutEscolha {
  return config?.layout === 'tag' ? 'tag' : 'list'
}

/** Protocolos que fazem sentido abrir a partir de um campo de link. */
const PROTOCOLOS = new Set(['http:', 'https:', 'mailto:', 'tel:'])

/**
 * Normaliza o que a pessoa digitou num endereco navegavel.
 *
 * Quem digita "orbind.com.br" quer dizer "https://orbind.com.br" — sem isso o
 * campo so aceitaria quem lembra de escrever o protocolo. Devolve `null` quando
 * nao da para entender como endereco.
 */
export function normalizarUrl(bruto: string): string | null {
  const texto = bruto.trim()
  if (!texto) return null
  // espaco no meio nunca e endereco; evita aceitar frase solta
  if (/\s/.test(texto)) return null
  const comProtocolo = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(texto) ? texto : `https://${texto}`
  let url: URL
  try {
    url = new URL(comProtocolo)
  } catch {
    return null
  }
  if (!PROTOCOLOS.has(url.protocol)) return null
  // http(s) exige um host com ponto (localhost e a excecao util em dev)
  if (url.protocol === 'http:' || url.protocol === 'https:') {
    const host = url.hostname
    if (!host || (!host.includes('.') && host !== 'localhost')) return null
    if (host.endsWith('.')) return null
  }
  return url.toString()
}

/** O texto e um endereco aceitavel? Vazio conta como valido (campo opcional). */
export function urlValida(bruto: string): boolean {
  if (!bruto.trim()) return true
  return normalizarUrl(bruto) !== null
}

/**
 * Monta o endereco de um campo BOTAO.
 *
 * O endereco e configurado no campo (nao digitado por registro) e pode citar
 * outros campos entre chaves — `https://busca/{cpf}` vira o CPF do proprio
 * registro, ja escapado. Marcador sem valor vira string vazia, entao o botao
 * nunca leva a chave crua para a URL.
 */
export function montarUrlDoBotao(template: string, valores: Record<string, unknown>): string | null {
  if (!template.trim()) return null
  const preenchido = template.replace(/\{([a-zA-Z][a-zA-Z0-9_]*)\}/g, (_, chave: string) => {
    const v = valores[chave]
    return v === undefined || v === null ? '' : encodeURIComponent(String(v))
  })
  return normalizarUrl(preenchido)
}

/** Marcadores `{chave}` citados por um endereco de botao. */
export function chavesDoTemplate(template: string): string[] {
  return [...template.matchAll(/\{([a-zA-Z][a-zA-Z0-9_]*)\}/g)].map((m) => m[1]!)
}

/**
 * Rotulo curto de uma URL para mostrar no campo — o endereco inteiro estoura a
 * largura e nao ajuda a reconhecer o destino.
 */
export function rotuloDaUrl(bruto: string): string {
  const url = normalizarUrl(bruto)
  if (!url) return bruto
  try {
    const u = new URL(url)
    if (u.protocol === 'mailto:' || u.protocol === 'tel:') return decodeURIComponent(u.pathname)
    const host = u.hostname.replace(/^www\./, '')
    const caminho = u.pathname === '/' ? '' : u.pathname
    return `${host}${caminho}${u.search}`
  } catch {
    return bruto
  }
}
